import {
  classifyCommand,
  coerceBashInput,
  coerceBashOutput,
  describeBash,
  humanize,
} from "./bash/interpret"
import type { NormalizedPart, NormalizedTool } from "./parts"
import { coerceReadInput, coerceReadOutput, describeRead } from "./read/interpret"
import { webFetchUrl } from "./utils/webFetch"

/** A short, present-tense label for the newest visible step in a live work group. */
export function latestWorkLabel(parts: readonly NormalizedPart[]): string {
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index]
    if (part?.kind === "tool") return toolProgressLabel(part.tool)
    // `reasoning-start` has no text. Keep the pre-token "Thinking" label through that lifecycle
    // chunk so the first reasoning delta does not look like a new row mounting.
    if (part?.kind === "reasoning") return "Thinking"
  }
  return "Working"
}

function toolProgressLabel(tool: NormalizedTool): string {
  if (tool.toolName === "web_fetch") {
    const domain = webFetchUrl(tool.input)?.hostname.replace(/^www\./, "") ?? "web page"
    if (tool.state === "output-error") return `Could not read ${domain}`
    if (tool.state === "output-available") return `Reviewing ${domain}`
    return `Reading ${domain}`
  }
  if (tool.toolName === "web_search") {
    if (tool.state === "output-error") return "Continuing after a web search failed"
    if (tool.state === "output-available") return "Reviewing web results"
    return "Searching the web"
  }
  if (tool.toolName === "bash") {
    if (tool.state === "input-streaming") return "Preparing a command"
    const input = coerceBashInput(tool.input)
    const command = input?.command ?? tool.inputText ?? ""
    const intent = classifyCommand(command)
    const description = describeBash(intent, coerceBashOutput(tool.output))
    return description.runningTitle
  }

  if (tool.toolName === "read") {
    const description = describeRead(coerceReadInput(tool.input), coerceReadOutput(tool.output))
    return `Reading ${description.target}`
  }

  const name = humanize(tool.toolName)
  return name ? `Using ${name}` : "Working"
}
